import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Stack } from "@mui/material";
import TeamMemberCard from "./TeamMemberCard";
import supabase from "../../../utils/supabase";

export default function AdminTeamMembers() {
  const { t, i18n, ready: translationsLoaded } = useTranslation(["adminTeam", "common"]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentLang = i18n.language;

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 4,
  };

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase.rpc('get_admin_team_with_stats');
        if (error) throw error;
        setTeamMembers(data || []);
      } catch (err) {
        console.error("Failed to fetch team members:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  if (loading || !translationsLoaded) {
    return <Typography>{t('loading', { ns: 'common' })}</Typography>;
  }
  if (error) {
    return <Typography color="error">{t('errorLoadingData', { ns: 'common' })}: {error}</Typography>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h2" sx={titleSx}>
        {t('memberListTitle', { ns: 'adminTeam' })}
      </Typography>
      <Typography variant="body1" sx={subtitleSx}>
        {t('memberListDescription', { ns: 'adminTeam' })}
      </Typography>
      <Stack direction="column" spacing={4} alignItems="center" sx={{ mt: 4 }}>
        {teamMembers.map((member) => {
          const memberInfo = {
            email: member.email,
            avatarUrl: member.avatar_url,
            name: member.display_name?.[currentLang] || member.display_name?.en || '',
            role: member.role?.[currentLang] || member.role?.en || '',
            affiliation: member.affiliation?.[currentLang] || member.affiliation?.en || '',
            description: member.description?.[currentLang] || member.description?.en || '',
            researcher: member.researcher,
          };
          return <TeamMemberCard key={member.id} memberInfo={memberInfo} />;
        })}
      </Stack>
    </Box>
  );
}
